import { Grid, Stack } from "@mui/material";
import { useCallback, useEffect } from "react";
import { Outlet } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed.tsx";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader.tsx";
import biApi from "../../../../api/biApi.ts";
import useClientContext from "../../../../context/useClientContext.ts";
import { BiApiClientProvider } from "../sv/contexts/BiApiClientProvider.tsx";
import type { BiApiClientBase } from "../sv/contexts/BiApiClientProviderContext.ts";
import ReportsManager from "./contexts/reports-manager/ReportsManager.tsx";
import { ReportsContextProvider } from "./contexts/reports/ReportsContextProvider.tsx";
import { GroupsContextProvider } from "./groups/contexts/GroupsContextProvider.tsx";
import useGroups from "./groups/hooks/useGroups.ts";
import useReports from "./hooks/useReports.ts";
import { getEditSystemReportUrl, getViewSystemReportUrl } from "./reports/utilities/editReportUrl.ts";

const CompanySystemReportsPage = () => {
  const { clientCode } = useClientContext();
  const loadReports = useCallback((signal?: AbortSignal) => biApi.systemReports.getReports({}, signal), []);
  const { reports, ui: reportsUi, actions: reportsActions } = useReports(loadReports, "System");
  const {
    groups,
    actions: groupActions,
    ui: groupsUi,
  } = useGroups({
    ...biApi.systemReportGroup,
    applyGroupSharing: reportsActions.applyGroupSharing,
  });

  useEffect(() => {
    groupActions.load(clientCode);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [clientCode, groupActions.load]);

  if (!reportsUi.loaded || !groupsUi.loaded) {
    return (
      <Stack sx={{ height: "100%" }}>
        <InlineLoader text="Loading reports..." />
      </Stack>
    );
  }

  if (reportsUi.error !== undefined || groupsUi.error !== undefined) {
    return (
      <Stack sx={{ height: "100%" }}>
        <DataLoadingFailed />
      </Stack>
    );
  }

  return (
    <Grid container sx={{ flex: 1, height: "100%", width: "100%" }}>
      <BiApiClientProvider<BiApiClientBase>
        api={{
          validateName: biApi.systemReports.validateReportName,
          getReportSharing: biApi.systemReports.getReportSharing,
          updateReportSharing: biApi.systemReports.updateReportSharing,
          getEditReportUrl: getEditSystemReportUrl,
          getViewReportUrl: getViewSystemReportUrl,
        }}
      >
        <ReportsContextProvider reports={reports} ui={reportsUi} actions={reportsActions}>
          <GroupsContextProvider
            groups={groups}
            ui={groupsUi}
            actions={{ ...groupActions, reloadReports: reportsActions.reloadReports }}
          >
            <ReportsManager reports={reports} reportUsage={"System"}>
              <Outlet />
            </ReportsManager>
          </GroupsContextProvider>
        </ReportsContextProvider>
      </BiApiClientProvider>
    </Grid>
  );
};

export default CompanySystemReportsPage;
